import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  Renderer2,
  ViewContainerRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faCircle,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { DialogCloseResult } from '@progress/kendo-angular-dialog';
import { NotificationService } from '@progress/kendo-angular-notification';
import { SVGIcon } from '@progress/kendo-svg-icons';
import { find, get } from 'lodash';
import { BehaviorSubject, take, takeUntil } from 'rxjs';
import { ASSESSMENT_INFO } from 'src/app/modules/evaluations/assessment/constants/assessment-routes';
import { RoutesConstantService } from 'src/app/services/routes-constant.service';
import { AbstractComponent } from 'src/app/shared/components/abstract-component';
import { dialogTimer } from 'src/app/shared/constants/dialog-specs';
import { MeetingSettingType } from 'src/app/shared/enums/meeting-setting-type';
import { MeetingType } from 'src/app/shared/enums/meeting-type';
import {
  NotificationAction,
  NotificationReadAllBit,
  NotificationTriggerPoint,
  NotificationType,
  TAKE_ONE,
} from 'src/app/shared/enums/notification';
import { AllNotificationsResponse } from '../../interface/get-all-notifications-response';
import { MarkPayload } from '../../interface/mark-payload';
import { DashboardService } from '../../services/dashboard.service';
import { MarkingWarnService } from '../../services/marking-warn.service';
import { MarkReadNotificationService } from '../../services/notification.service';
import { MeetingRole } from 'src/app/shared/interfaces/meeting-role';
import { PwnType } from 'src/app/modules/pwn/shared/enums/pwn-type';
import { PWN_INFO } from 'src/app/modules/pwn/constants/pwn-routes';

import { LiveRegionService } from 'src/app/shared/services/live-region.service';

@Component({
  selector: 'app-notification-content',
  templateUrl: './notification-content.component.html',
  styleUrls: ['./notification-content.component.scss'],
  standalone: false,
})
export class NotificationContentComponent
  extends AbstractComponent
  implements AfterViewInit
{
  notificationType: typeof NotificationType = NotificationType;
  notificationAction: typeof NotificationAction = NotificationAction;
  circle: SVGIcon = this.customFaIcon(faCircle as IconDefinition);
  triangle: SVGIcon = this.customFaIcon(
    faTriangleExclamation as IconDefinition,
  );
  @Input() content: AllNotificationsResponse[] = [];
  @Input() isAllNotificationView: boolean = false;
  dataNycId: string;
  meetingRoster: MeetingRole[] = [
    {
      label: 'SCHOOL SOCIAL WORKERS (MANDATED SERVICES)',
      title: 'SCHOOL SOCIAL WORKERS (MANDATED SERVICES)',
      isRequired: true,
      isSticky: true,
      members: [],
    },
  ];
  meetingTypeENUM: typeof MeetingType = MeetingType;
  referralSk: string;
  iepMeetingActionText: string = 'Schedule IEP Meeting';
  showScheduleMeetingDialog$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  caseStage: string;

  constructor(
    library: FaIconLibrary,
    private markingWarnService: MarkingWarnService,
    private routesConstantService: RoutesConstantService,
    private router: Router,
    private dashboardService: DashboardService,
    private notificationService: NotificationService,
    private markReadNotificationService: MarkReadNotificationService,
    private liveRegionService: LiveRegionService,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private vcr: ViewContainerRef,
  ) {
    super();
    library.addIcons(faTriangleExclamation);
  }

  ngAfterViewInit(): void {
    const parent = this.elementRef?.nativeElement?.parentElement;
    if (
      parent &&
      (parent.getAttribute('role') === 'tabpanel' ||
        parent.classList?.contains('k-tabstrip-content'))
    ) {
      this.renderer.setAttribute(parent, 'tabindex', '-1');
    }
  }
  customFaIcon(icon: IconDefinition): SVGIcon {
    return {
      name: icon.iconName,

      content: `<path d="${icon.icon[4]}" />`,

      viewBox: `0 0 ${icon.icon[0]} ${icon.icon[1]}`,
    };
  }

  getMarkButtonAriaLabel(
    dataItem: AllNotificationsResponse,
    index?: number,
  ): string {
    const actionText =
      dataItem?.type === NotificationType.ActionRequired
        ? 'Mark as done'
        : 'Mark as red';
    const studentId = dataItem?.nycID || dataItem?.studentSK;
    if (studentId) {
      return `${actionText} for Student ${studentId}`;
    }
    if (index !== undefined && index !== null) {
      return `${actionText} for row ${index + 1}`;
    }
    return actionText;
  }

  getViewButtonAriaLabel(
    dataItem: AllNotificationsResponse,
    index?: number,
  ): string {
    const studentId = dataItem?.nycID || dataItem?.studentSK;
    if (studentId) {
      return `View for Student ${studentId}`;
    }
    if (index !== undefined && index !== null) {
      return `View for row ${index + 1}`;
    }
    return 'View';
  }

  // Mark the individual notification as read.
  markingNotification(
    payload: MarkPayload[],
    isViewAction?: boolean,
    notificationData?: AllNotificationsResponse,
  ): void {
    this.dashboardService
      .markNotification(payload)
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe({
        next: () => {
          if (isViewAction) {
            this.viewNotification(notificationData);
          } else {
            this.notificationService.show({
              content: 'Marking notifications successful.',
              cssClass: 'notification-success',
              hideAfter: dialogTimer,
              position: { horizontal: 'center', vertical: 'top' },
              animation: { type: 'slide', duration: 60 },
              type: { style: 'success', icon: true },
              closable: true,
            });
          }
          //Refresh the notification list
          this.markReadNotificationService.notifyNotificationMarked(
            isViewAction,
          );
          // this.getNotificationSummary.emit();
        },
        error: () => {
          this.notificationService.show({
            content: 'Marking notifications failed. Please try again later.',
            cssClass: 'notification-error',
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'slide', duration: 60 },
            type: { style: 'error', icon: true },
            closable: true,
          });
        },
      });
  }

  public mark(
    notificationData: AllNotificationsResponse,
    isViewAction?: boolean,
  ): void {
    if (
      isViewAction &&
      notificationData.notificationStatus === NotificationAction.Read
    ) {
      // If already read, just allow user to view
      this.viewNotification(notificationData);
      return;
    }
    const payload: MarkPayload[] = [
      {
        notificationSk: notificationData.notificationSk,
        studentSk: notificationData.studentSK,
        notificationTypeId: notificationData.notificationTypeId,
        notificationStatus: NotificationAction.Read,
        markAllRead: NotificationReadAllBit.None,
        authUserRoleLocationSk: notificationData.authUserRoleLocationSk,
      },
    ];

    this.markingWarnService.dontShowAgainWarn$
      .pipe(take(TAKE_ONE))
      .subscribe((dontShow: boolean) => {
        if (!dontShow && !isViewAction) {
          // Show warning dialog before marking
          this.markingWarnService
            .open('markAll', this.vcr)
            .pipe(takeUntil(this.componentDestroyed))
            .subscribe((result: DialogCloseResult | { donotshow: boolean }) => {
              if (!(result instanceof DialogCloseResult)) {
                if (result?.donotshow) {
                  this.markingWarnService.setDontShowWarnAgain(
                    result.donotshow,
                  );
                }
                this.markingNotification(
                  payload,
                  isViewAction,
                  notificationData,
                );
              }
            });
        } else {
          // Warning suppressed, directly mark notification
          this.markingNotification(payload, isViewAction, notificationData);
        }
      });
  }

  override afterOnDestroy(): void {
    // Any additional cleanup can be done here
  }

  viewNotification(dataItem: AllNotificationsResponse): void {
    const route: string = this.evaluationDataRoute(dataItem?.evaluationType);
    switch (dataItem.notificationTypeId) {
      case NotificationTriggerPoint.AdditionalAssessmentApprovalNeeded:
        // Handle route for AdditionalAssessmentApprovalNeeded notification
        this.router.navigate([
          this.routesConstantService.routes.evaluations +
            dataItem.referralSk +
            '/' +
            dataItem?.nycID +
            '/' +
            dataItem.caseActionSk +
            '/edit/pwn/request-additional-assessments',
        ]);
        break;
      case NotificationTriggerPoint.AdditionalAssessmentApprovalNeededDuplicate:
        // Handle route for AdditionalAssessmentApprovalNeededDuplicate notification
        this.router.navigate([
          this.routesConstantService.routes.evaluations +
            dataItem.referralSk +
            '/' +
            dataItem?.nycID +
            '/' +
            dataItem.caseActionSk +
            '/view/pwn/request-additional-assessments',
        ]);
        break;
      case NotificationTriggerPoint.AssessmentReportSubmittedByDOEEvaluator:
      case NotificationTriggerPoint.AssessmentReportSubmittedByContractedEvaluator:
      case NotificationTriggerPoint.AssessmentReportApprovedByContractAgencySupervisor:
        if (route) {
          this.router.navigate([
            this.routesConstantService.routes.evaluations +
              dataItem?.referralSk +
              '/' +
              dataItem?.evaluationSk +
              '/' +
              dataItem?.nycID +
              '/reviewassessment/' +
              route,
          ]);
        }
        break;
      case NotificationTriggerPoint.AssessmentReportRejectedByIndisciplineSupervisor:
        this.router.navigate([
          this.routesConstantService.routes.evaluations +
            dataItem.referralSk +
            '/' +
            dataItem.evaluationSk +
            '/' +
            dataItem.nycID +
            '/edit/' +
            route,
        ]);
        break;
      case NotificationTriggerPoint.IEPMeetingNotScheduledWithin30DaysAfterConsent:
        // Handle route for IEPMeetingNotScheduledWithin30DaysAfterConsent notification
        //On click Route to IEP Document Meeting Schedule
        this.dataNycId = dataItem.nycID;
        this.referralSk = dataItem.referralSk;
        this.caseStage =
          dataItem.caseStage ?? MeetingSettingType.Initial?.toString(); // If N/A make it to 1 default
        this.openScheduleMeetingDialog();
        break;
      case NotificationTriggerPoint.NoFADWithin30DaysOfSEITAssignment:
      case NotificationTriggerPoint.ChildrenAwaitingPlacementSpecialClassSCIS:
        // Handle route for NoFADWithin30DaysOfSEITAssignment notification
        //Student Profile - Service Details tab
        this.router.navigate([/student/ + dataItem?.nycID]);
        break;
      case NotificationTriggerPoint.ArrangeProgramAndServicesFirstCome:
        // Handle route for ArrangeProgramAndServicesFirstCome notification
        //No route
        break;
      case NotificationTriggerPoint.STAC5Submitted:
        // Handle route for STAC5Submitted notification
        // Route to dashboard, filtered by student name ==> No  route
        this.dashboardService.setNotificationReloadData(dataItem.nycID);
        if (!this.router.url.includes('dashboard')) {
          this.router.navigate(['/dashboard']);
        } else this.dashboardService.getReloadDashboardGrid(true);
        break;
      case NotificationTriggerPoint.StudentAddedToWaitlist:
        // Handle route for StudentAddedToWaitlist notification
        this.router.navigate(['/wait-list/MDE/' + dataItem?.caseActionSk]);
        break;
      case NotificationTriggerPoint.AssessmentAssignedToEvaluator:
        // Handle route for AssessmentAssignedToEvaluator notification
        if (route) {
          this.router.navigate([
            this.routesConstantService.routes.evaluations +
              dataItem?.referralSk +
              '/' +
              dataItem?.evaluationSk +
              '/' +
              dataItem?.nycID +
              '/edit/' +
              route,
          ]);
        }
        break;
      case NotificationTriggerPoint.AssessmentAssignedToSecondaryAgency:
        // Handle route for AssessmentAssignedToSecondaryAgency notification
        //On Click: Route the user to assignment screen. // assessment planning screen
        this.router.navigate([
          this.routesConstantService.routes.evaluations +
            dataItem.referralSk +
            '/' +
            dataItem.nycID +
            '/edit/assessment-planning',
        ]);
        break;
      case NotificationTriggerPoint.ProviderRejectsMandate:
        this.router.navigate([
          this.routesConstantService.routes.dashboard +
            '/awaiting-provider-assignment',
        ]);
        break;
      case NotificationTriggerPoint.DeclineAssignment:
        this.router.navigate([
          this.routesConstantService.routes.dashboard +
            '/request-for-assistance',
        ]);
        break;
      case NotificationTriggerPoint.IEPFinalized:
        // if (iepType === IepType.CSP) {
        //   this.router.navigate([
        //     this.routesConstantService.routes.addiepdocument +
        //     this.nycId +
        //     '/' +
        //     this.iepSk +
        //     '/view/Recommended-program-and-services-CSP',
        //   ]);
        // } else {
        this.router.navigate([
          this.routesConstantService.routes.addiepdocument +
            dataItem?.nycID +
            '/' +
            dataItem?.caseActionSk +
            '/view/eligibility',
        ]);
        // }
        break;
      case NotificationTriggerPoint.RejectRequestForInitialReferral:
        this.router.navigate([
          this.routesConstantService.routes.referrals +
            dataItem?.referralSk +
            '/' +
            dataItem?.nycID +
            '/add/pwn/refusal',
        ]);
        break;
      case NotificationTriggerPoint.CreationOfReevaluation:
        this.router.navigate(
          [
            this.routesConstantService.routes.referrals +
              dataItem?.nycID +
              '/add/request-for-reevaluation',
          ],
          {
            queryParams: { referralType: dataItem?.referralType },
          },
        );
        break;
      case NotificationTriggerPoint.SpecializedSchoolAndLREFormSubmitted:
        this.router.navigate([
          this.routesConstantService.routes.addiepdocument +
            'lre' +
            '/' +
            dataItem?.nycID +
            '/' +
            dataItem?.caseActionSk +
            '/add',
        ]);
        break;
      case NotificationTriggerPoint.ProviderRetroactivelyAssignedByLocationSupervisor:
        this.router.navigate([
          this.routesConstantService.routes.dashboard +
            '/awaiting-service-confirmation',
        ]);
        break;
      case NotificationTriggerPoint.OutreachDeadlineRequestSubmittedToSecondaryComp3Agencies:
        this.router.navigate([
          this.routesConstantService.routes.dashboard + '/outreach-by-deadline',
        ]);
        break;
      case NotificationTriggerPoint.RequestLocationProvider:
        this.router.navigate([
          this.routesConstantService.routes.dashboard +
            '/awaiting-provider-assignment',
        ]);
        break;
      case NotificationTriggerPoint.TemporaryShortTermProviderAssigned:
        this.router.navigate([
          this.routesConstantService.routes.dashboard +
            '/awaiting-service-confirmation',
        ]);
        break;
      case NotificationTriggerPoint.InitialReferralSubmit:
      case NotificationTriggerPoint.AcceptRequestForInitialReferral:
        this.router.navigate([
          this.routesConstantService.routes.referrals +
            this.referralSk +
            '/' +
            dataItem?.nycID +
            '/add/pwn/' +
            this.getPwnUrl(dataItem?.pwnType),
        ]);
        break;
      case NotificationTriggerPoint.RequestForInitialReferralSubmit:
        this.router.navigate([
          `${this.routesConstantService.routes.newReferralsRequestPage}/${dataItem?.nycID}`,
        ]);
        break;

      default:
        // Handle unknown notification type
        this.dashboardService.setNotificationId(dataItem.notificationTypeId);
        this.router.navigate([/student/ + dataItem?.nycID]);
        break;
    }
  }

  evaluationDataRoute(evaluationType: number): string {
    return get(
      find(ASSESSMENT_INFO, { evaluationID: evaluationType }),
      'route',
    );
  }

  showViewButton(dataItem: AllNotificationsResponse): boolean {
    return (
      dataItem.type === NotificationType.ActionRequired &&
      dataItem.actions === NotificationAction.Created
    );
  }

  isViewButtonDisabled(dataItem: AllNotificationsResponse): boolean {
    return (
      dataItem.actions !== NotificationAction.UnRead &&
      dataItem.actions !== NotificationAction.Created
    );
  }

  openScheduleMeetingDialog(): void {
    this.showScheduleMeetingDialog$.next(true);
  }

  closeScheduleMeetingDialog(): void {
    this.showScheduleMeetingDialog$.next(false);
  }

  getPwnUrl(pwnType: number): string {
    let pwnUrl: string = '';
    switch (pwnType) {
      case PwnType.RequestForReEval:
        pwnUrl = PWN_INFO.proposedRequestReevaluation.route;
        break;
      case PwnType.AdditionalReEval:
        pwnUrl = PWN_INFO.requestForAdditionalReevaluation.route;
        break;
      case PwnType.RefusalReEval:
        pwnUrl = PWN_INFO.refusalOfRequestForReevaluation.route;
        break;
      case PwnType.RefusalAdditionalReEval:
        pwnUrl = PWN_INFO.refusalOfRequestForAdditionalReevaluation.route;
        break;
      case PwnType.Mandated3YearReEval:
        pwnUrl = PWN_INFO.mandatedThreeYearReevaluation.route;
        break;
      case PwnType.ParentWaiverMandated3YearReEval:
        pwnUrl = PWN_INFO.parentWaiverMandatedThreeYearReevaluation.route;
        break;
      case PwnType.Initial:
        pwnUrl = PWN_INFO.initial.route;
        break;
      case PwnType.ReevaluationAnnual:
        pwnUrl = PWN_INFO.reevaluationAnnual.route;
        break;
      case PwnType.NoticeOfIR:
        pwnUrl = PWN_INFO.noticeOfIr.route;
        break;
      case PwnType.Refusal:
        pwnUrl = PWN_INFO.refusal.route;
        break;
      case PwnType.PreSchool:
        pwnUrl = PWN_INFO.preSchool.route;
        break;
      case PwnType.ParentallyPlaced:
        pwnUrl = 'parentally-placed';
        break;
      case PwnType.WithdrawalInitialReferralFromSpecialEducationProcess:
        pwnUrl = 'withdrawal';
        break;
    }
    return pwnUrl;
  }
}
